import { Chain } from "@nitro-ape/shared/models/enums/chain";
import { clsx, type ClassValue } from "clsx";
import { JSX } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export function generateColorFromString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = hash % 360;
  return `hsl(${hue}, 65%, 50%)`;
}
export function shortenAddress(
  address: string,
  startLen = 5,
  endLen = 4,
): string {
  if (!address) return "";
  const start = address.substring(0, startLen);
  const end = address.substring(address.length - endLen);
  return `${start}...${end}`;
}
export const humanize = (
  value: number | "N/A",
  showNa = false,
  smallNumbersFixed = 3,
  decimals?: number,
): string => {
  if (value === "N/A") return "-";
  if (value === 0 && showNa) return "-";
  const subscripts = ["", "", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"];

  if (value === 0) return "0";

  // Convert scientific notation to regular decimal string (positive values)
  let str = value.toString();
  if (str.includes("e-")) {
    const [coefficient, exponent] = str.split("e-");
    const decimalPlaces = parseInt(exponent);
    str = "0." + "0".repeat(decimalPlaces - 1) + coefficient.replace(".", "");
  }

  // Handle very small numbers
  if (value < 1 && value > 0) {
    const match = /^0\.(0+)(\d)/.exec(str); // Match leading zeros after the decimal and first significant digit

    if (match) {
      // For numbers with leading zeros after decimal (e.g., 0.00123)
      const leadingZerosCount = match[1].length;
      const significantDigit = match[2];
      const remainingDigits = str.slice(match[0].length);
      const formatted = `0.0${subscripts[leadingZerosCount]}${significantDigit}${remainingDigits.slice(0, smallNumbersFixed)}`;
      if (formatted.includes("undefined")) {
        return "0";
      }
      return formatted;
    } else {
      // For regular decimals (e.g., 0.327799939)
      return value.toFixed(decimals ?? 4);
    }
  }

  // Handle very small negative numbers symmetrically
  if (value > -1 && value < 0) {
    const absVal = Math.abs(value);
    let absStr = absVal.toString();
    if (absStr.includes("e-")) {
      const [coefficient, exponent] = absStr.split("e-");
      const decimalPlaces = parseInt(exponent);
      absStr =
        "0." + "0".repeat(decimalPlaces - 1) + coefficient.replace(".", "");
    }

    const match = /^0\.(0+)(\d)/.exec(absStr);
    if (match) {
      const leadingZerosCount = match[1].length;
      const significantDigit = match[2];
      const remainingDigits = absStr.slice(match[0].length);
      const formatted = `0.0${subscripts[leadingZerosCount]}${significantDigit}${remainingDigits.slice(0, smallNumbersFixed)}`;
      if (formatted.includes("undefined")) {
        return "0";
      }
      return `-${formatted}`;
    } else {
      const fixed = absVal.toFixed(decimals ?? 4);
      return `-${fixed}`;
    }
  }

  // Handle large numbers (default format)
  return formatToReadableAmount(value, decimals ?? 2);
};

export const formatToReadableAmount = (amount: number, decimals: number) => {
  const units = ["", "K", "M", "B", "T"];
  let unitIndex = 0;
  let formattedAmount = Number(amount);

  // Keep dividing by 1000 until we either run out of units or the number is less than 1000
  while (formattedAmount >= 999.5 && unitIndex < units.length - 1) {
    formattedAmount /= 1000;
    unitIndex++;
  }

  let result = "";
  if (unitIndex > 0) {
    result = formattedAmount.toFixed(decimals) + units[unitIndex];
  } else {
    result = formattedAmount.toFixed(decimals);
  }
  return result.replace(/\.0+([a-zA-Z]*)$/, "$1");
};

export const formatResolution = (resolution: string): string => {
  if (/^\d+$/.test(resolution)) {
    const minutes = parseInt(resolution);
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      // Convert to hours if 60 or more minutes
      const hours = minutes / 60;
      return `${hours}h`;
    }
  }
  return resolution.toLowerCase();
};

export function mapSeparated<T>(
  items: T[],
  mapItem: (item: T, index: number) => JSX.Element,
  mapSeparator: (item: T, index: number) => JSX.Element,
): JSX.Element[] {
  if (!items || items.length === 0) return [];

  const result: JSX.Element[] = [];
  for (let i = 0; i < items.length; i++) {
    result.push(mapItem(items[i], i));
    if (i < items.length - 1) {
      result.push(mapSeparator(items[i], i));
    }
  }
  return result;
}

export function formatPercents(
  percents: number | undefined,
  addPlus = false,
  showSmallValues = true,
  formatAsInteger = false,
) {
  if (percents === undefined) return "-";

  if (formatAsInteger) {
    if (percents > 0 && percents < 1) {
      return "< 1%";
    }
    const rounded = Math.round(percents);
    return `${addPlus && percents > 0 ? "+" : ""}${rounded}%`;
  }

  if (percents < 0.01 && percents > 0 && !showSmallValues) {
    return "<0.01%";
  }

  let formatted = percents.toFixed(2);

  if (formatted.endsWith("00")) {
    formatted = percents.toFixed(0);
  }

  return `${addPlus && percents > 0 ? "+" : ""}${formatted}%`;
}

export function formatDuration(startDate: Date, endDate: Date): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  let days = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  );
  let hours = end.getHours() - start.getHours();
  let minutes = end.getMinutes() - start.getMinutes();

  if (minutes < 0) {
    minutes += 60;
    hours -= 1;
  }
  if (hours < 0) {
    hours += 24;
    days -= 1;
  }

  if (days > 0) {
    return hours > 0 ? `${days}D ${hours}H` : `${days}D`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}H ${minutes}M` : `${hours}H`;
  }
  return `${minutes}M`;
}

export function isAddressValid(address: string, chain: Chain) {
  switch (chain) {
    case Chain.ethereum:
    case Chain.sei:
      return /^0x[a-fA-F0-9]{40}$/.test(address);
    case Chain.solana:
      return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
    default:
      return false;
  }
}


interface ColorStop {
  color: string;
  position?: number;
}

interface GradientDefinition {
  angle?: number;
  stops: ColorStop[];
}

export const getLevelColor = (level: number): GradientDefinition => {
  switch (level) {
    case 1:
      return { stops: [{ color: "#88939E" }] };
    case 2:
      return { stops: [{ color: "#51FF71" }] };
    case 3:
      return { stops: [{ color: "#0BA9FF" }] };
    case 4:
      return { stops: [{ color: "#B626FF" }] };
    case 5:
      return { stops: [{ color: "#FFE500" }] };
    case 6:
      return { stops: [{ color: "#FF6F09" }] };
    case 7:
      return {
        angle: 90,
        stops: [
          { color: "#FF3B30", position: 0 },
          { color: "#FFCE09", position: 51.92 },
          { color: "#FF3B30", position: 100 },
        ],
      };
    case 8:
      return {
        angle: 90,
        stops: [
          { color: "#FF00DD", position: -1.79 },
          { color: "#25FFED", position: 39.07 },
          { color: "#9E17FF", position: 82.06 },
        ],
      };
    default:
      return { stops: [{ color: "#88939E" }] };
  }
};
